import { useState } from "react";
import { Card } from "../../../../components/ui/card";
import {
  ChevronDown,
  ChevronUp,
  Clock,
  TrendingUp,
  AlertTriangle,
} from "lucide-react";

export default function PriorityActionCard({
  action,
  interval,
  highCount = 0,
  onViewAll,
}) {
  const [expanded, setExpanded] = useState(false);

  if (!action) return null;

  const isHigh = action.priority === "high";

  return (
    <Card
      className={`
        p-6 rounded-2xl border
        ${isHigh
          ? "border-yellow-500/40 bg-yellow-500/5"
          : "border-border/40 bg-muted/20"}
      `}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="text-sm font-semibold text-yellow-400">
          🔥 Priority Action ({interval})
        </div>

        {highCount > 1 && (
          <span className="text-xs text-yellow-300 bg-yellow-500/10 px-2 py-0.5 rounded">
            High priority × {highCount}
          </span>
        )}
      </div>

      {/* Title */}
      <h3 className="text-xl font-semibold text-white">
        {action.title}
      </h3>

      {/* Meta */}
      <div className="flex flex-wrap gap-4 mt-2 text-sm">
        {action.timing?.deadline_label && (
          <span className="flex items-center gap-1 text-amber-300">
            <Clock className="w-4 h-4" />
            {action.timing.deadline_label}
          </span>
        )}

        {action.expected_profit_impact != null && (
          <span className="flex items-center gap-1 text-emerald-400 font-semibold">
            <TrendingUp className="w-4 h-4" />
            +{action.expected_profit_impact}
          </span>
        )}
      </div>

      {/* Summary */}
      {!expanded && (
        <p className="text-sm text-neutral-300 mt-3 line-clamp-2 max-w-[85%]">
          {action.detail}
        </p>
      )}

      {/* Expanded */}
      {expanded && (
        <div className="mt-4 space-y-3 text-sm text-neutral-300">
          <p>{action.detail}</p>

          {Array.isArray(action.expected_impacts) && (
            <ul className="list-disc pl-5">
              {action.expected_impacts.map((imp, idx) => (
                <li key={idx}>{imp}</li>
              ))}
            </ul>
          )}

          {action.timing?.if_delayed && (
            <div className="flex gap-2 text-red-400">
              <AlertTriangle className="w-4 h-4 mt-0.5" />
              <span>{action.timing.if_delayed}</span>
            </div>
          )}
        </div>
      )}

      {/* Footer */}
      <div className="flex justify-between items-center mt-5">
        <button
          onClick={() => setExpanded(!expanded)}
          className="text-sm text-yellow-400 flex items-center gap-1"
        >
          {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          {expanded ? "Hide details" : "View details"}
        </button>

        <button
          onClick={onViewAll}
          className="text-sm font-medium text-yellow-400 hover:text-yellow-300"
        >
          View all actions →
        </button>
      </div>
    </Card>
  );
}